import React, { useState } from "react";
import { Header, Sidebar, SidebarSection, SidebarItem, Tabs, BrandLockup, TextInput, Button, Avatar } from "components";
import { Stage, SearchIcon, PlusIcon, SparkleIcon, demoTriggerBtn } from "./_shared";

const TabsDemo = () => {
  const [v, setV] = useState("all");
  return <Tabs value={v} onChange={setV} items={[{ value: "all", label: "All", count: 8 }, { value: "drafts", label: "Drafts", count: 3 }, { value: "ready", label: "Ready", count: 2 }, { value: "played", label: "Played", count: 3 }]} />;
};
const HeaderDemo = () => (
  <Header
    brand={<BrandLockup size="sm" />}
    search={<TextInput leftIcon={<SearchIcon />} placeholder="Search games…" />}
    actions={<Button variant="primary" startIcon={<PlusIcon />}>New Game</Button>}
    trailing={<Avatar name="Jamie L" size="sm" />}
  />
);
const SidebarDemo = () => (
  <Sidebar width={220} style={{ height: 280, borderRadius: 10 }}>
    <SidebarSection label="Library">
      <SidebarItem label="All Games" count={8} active />
      <SidebarItem label="Drafts" count={3} dot="var(--color-orange)" />
      <SidebarItem label="Ready" count={2} dot="var(--color-teal)" />
      <SidebarItem label="Played" count={3} dot="#94a3b8" />
      <SidebarItem label="Archive" count={4} />
    </SidebarSection>
    <SidebarSection label="Create">
      <SidebarItem label="Blank" icon={<PlusIcon />} />
      <SidebarItem label="AI generate" icon={<SparkleIcon />} />
    </SidebarSection>
  </Sidebar>
);
const SidebarDrawerDemo = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button style={demoTriggerBtn} onClick={() => setOpen(true)}>Open drawer…</button>
      <Sidebar drawer open={open} onClose={() => setOpen(false)} width={240}>
        <SidebarSection label="Library">
          <SidebarItem label="All Games" count={8} active onClick={() => setOpen(false)} />
          <SidebarItem label="Drafts" count={3} dot="var(--color-orange)" onClick={() => setOpen(false)} />
          <SidebarItem label="Ready" count={2} dot="var(--color-teal)" onClick={() => setOpen(false)} />
          <SidebarItem label="Played" count={3} dot="#94a3b8" onClick={() => setOpen(false)} />
        </SidebarSection>
      </Sidebar>
    </>
  );
};

export const navigationEntries = [
  {
    id: "header", name: "Header", group: "Navigation", status: "live",
    description: "App-shell top bar — slotted brand / search / actions / trailing. Distinct from the marketing AppBar header.",
    props: [{ name: "brand / search / actions / trailing", type: "node", description: "Slots" }],
    variants: [{ label: "Dashboard header", render: () => <Stage width={620}><HeaderDemo /></Stage> }],
    code: `import { Header, BrandLockup, TextInput, Button, Avatar } from "components";

<Header
  brand={<BrandLockup size="sm" />}
  search={<TextInput leftIcon={<SearchIcon />} placeholder="Search games…" />}
  actions={<Button variant="primary" startIcon={<PlusIcon />}>New Game</Button>}
  trailing={<Avatar name="Jamie L" size="sm" />}
/>`,
  },
  {
    id: "sidebar", name: "Sidebar / SidebarSection / SidebarItem", group: "Navigation", status: "live",
    description: "Left-rail nav: sections, items (icon + label + count + active + dot), dividers. Static rail by default, or an off-canvas slide-in drawer.",
    props: [
      { name: "drawer", type: "bool", default: "false", description: "Off-canvas slide-in mode" },
      { name: "open / onClose", type: "—", description: "Drawer visibility + dismiss" },
      { name: "SidebarItem.active", type: "bool", description: "Active state (teal)" },
      { name: "SidebarItem.dot", type: "color", description: "Status dot instead of icon" },
      { name: "SidebarItem.count", type: "number", description: "Trailing count" },
    ],
    variants: [
      { label: "Static rail", render: () => <Stage><SidebarDemo /></Stage> },
      { label: "Drawer (slide-in)", render: () => <Stage><SidebarDrawerDemo /></Stage> },
    ],
    code: `import { Sidebar, SidebarSection, SidebarItem } from "components";

<Sidebar>
  <SidebarSection label="Library">
    <SidebarItem label="All Games" count={8} active onClick={selectAll} />
    <SidebarItem label="Drafts" count={3} dot="var(--color-orange)" />
  </SidebarSection>
</Sidebar>

// Off-canvas drawer:
<Sidebar drawer open={open} onClose={() => setOpen(false)}>…</Sidebar>`,
  },
  {
    id: "tabs", name: "Tabs (FilterTabs)", group: "Navigation", status: "live",
    description: "Horizontal filter tabs with count badges and a teal active underline.",
    props: [{ name: "items / value / onChange", type: "—", description: "Controlled" }],
    variants: [{ label: "Filter tabs", render: () => <Stage width={420}><TabsDemo /></Stage> }],
    code: `import { Tabs } from "components";

const [filter, setFilter] = useState("all");

<Tabs
  value={filter}
  onChange={setFilter}
  items={[
    { value: "all", label: "All", count: 8 },
    { value: "drafts", label: "Drafts", count: 3 },
    { value: "ready", label: "Ready", count: 2 },
    { value: "played", label: "Played", count: 3 },
  ]}
/>`,
  },
  {
    id: "brand-lockup", name: "BrandLockup", group: "Navigation", status: "live",
    description: "The canonical Groupardy logo — official SVG mark + “GROUPARDY” wordmark in Helvetica Inserat with the layered teal/gold shadow (per brand-book.html). The single brand-logo component; also exports BrandMark (mark only) and BrandWordmark.",
    props: [
      { name: "size", type: "'xs'|'sm'|'md'|'lg'|'xl'", default: "'md'", description: "Scale" },
      { name: "variant", type: "'light'|'dark'|'teal'", default: "'light'", description: "Wordmark color" },
      { name: "orientation", type: "'horizontal'|'vertical'", default: "'horizontal'", description: "Mark/wordmark stacking" },
      { name: "showWordmark", type: "bool", default: "true", description: "Mark + wordmark, or mark only" },
    ],
    variants: [
      { label: "Sizes", render: () => <Stage><div style={{ display: "flex", gap: 24, alignItems: "flex-end" }}><BrandLockup size="sm" /><BrandLockup size="md" /><BrandLockup size="lg" /></div></Stage> },
      { label: "Vertical · on dark", render: () => <Stage surface="game"><BrandLockup size="lg" variant="dark" orientation="vertical" /></Stage> },
      { label: "Mark only", render: () => <Stage><BrandLockup size="md" showWordmark={false} /></Stage> },
    ],
    code: `import { BrandLockup } from "components";

<BrandLockup size="md" />
<BrandLockup size="lg" variant="dark" orientation="vertical" />
<BrandLockup size="sm" showWordmark={false} />  // mark only`,
  },
];
